
// Contracts for TravelRepublic.Manager.Bookings
// --------------------------------------------------
namespace Core.ManageBooking {

    export enum TripStage {
        PreTrip,
        OnTrip,
        PostTrip
    }

    export class BookedTrip {
        AdHocBooking: AdHocBooking;
        Adults: number;
        AirportParkingBooking: AirportParkingBooking;
        AttractionsBooking: AttractionsBooking;
        BundleBooking: BundleBooking;
        CarHireBooking: CarHireBooking;
        Children: number;
        CruiseBooking: CruiseBooking;
        EndDate: string;
        FlightBooking: FlightBooking;
        HotelBooking: HotelBooking;
        Infants: number;
        InsuranceBooking: TravelInsuranceBooking;
        MarhabaBooking: MarhabaBooking;
        StartDate: string;
        TransferBooking: TransferBooking;

        // decorated properties
        DaysUntilDeparture: number = 0;
        DaysUntilReturn: number = 0;
        Destination: Destinations.DestinationSummary;
        Duration: number = 0;
        MainBookingId: number = 0;
        PaymentDetails: PaymentDetails = new PaymentDetails();
        TripStage: TripStage;
    }

    export class PaymentDetails {
        Balances: BalanceDue[] = [];
        CurrencyCode: string;
        Paid: number = 0;
        PayDirect: DirectPayment[] = [];
        Outstanding: number = 0;
        Total: number = 0;
    }

    export class BalanceDue {
        Amount: number = 0;
        BookingType: BookingType;
        CurrencyCode: string;
        Due: string;
    }

    export class DirectPayment {
        Amount: number = 0;
        BookingType: BookingType;
        LocalAmount: number = 0;
        LocalCurrency: string;
    }

    export interface ProductBooking {
        readonly BookingId: number;
        readonly BookingRef: string;
        readonly BookingType: number;
        readonly DomainId: number;
        readonly EndDate: string;
        readonly PackageId: string;
        PaymentInfo :PaymentInformation;
        readonly PriceBreakdown : PriceBreakdown[];
        readonly StartDate: string;
        readonly Status: BookingStatus;
    }

    export interface BundleBooking extends ProductBooking {
    }

    export interface HotelBooking extends ProductBooking {
        readonly Hotel: Hotel;
        readonly CheckInDate: string;
        readonly CheckOutDate: string;
        readonly Rooms: Room[];
        readonly ReviewedByCustomer: boolean;
        Duration?: number;
    }

    export interface Hotel {
        readonly EstabId: number;
        readonly Title: string;
        readonly StarRating: number;
        readonly Latitude: number;
        readonly Longitude: number;
        readonly CustomerRating: number;
        readonly CustomerRatingTitle: string;
        readonly CustomerRatingCount: number;
        readonly Url: string;
        readonly Destinations: Destinations.DestinationSummary[];
        // decorated properties
        ImageUrl: string;
    }

    export interface Room {
        readonly Title: string;
        readonly BoardType: number;
        readonly Adults: number;
        readonly ChildAges: number[];
    }

    export interface FlightBooking extends ProductBooking {
        readonly Legs: FlightLeg[];
    }

    export interface FlightLeg {
        readonly ArrivalAirport: AirportSummary;
        readonly ArrivalTime: string;
        readonly Cabin: number;
        readonly Carrier: string;
        readonly DepartureAirport: AirportSummary;
        readonly DepartureTime: string;
        readonly FlightNo: string;
        readonly IsOutbound: boolean;

        DayDiff: number;
    }

    export interface AirportSummary {
        readonly Title: string;
        readonly Code: string;
        readonly Destination: Destinations.DestinationSummary;
    }

    export interface TransferBooking extends ProductBooking {
        Outbound?: TransferLeg;
        Return?: TransferLeg;

        Legs: TransferLeg[];
        Title: string;
    }

    export interface TransferLeg {
        readonly Date: string;
        readonly DropOffPoint: string;
        readonly PickUpPoint: string;
        readonly Title: string;
    }

    export interface CarHireBooking extends ProductBooking {
        readonly CarDescription: string;
        readonly DropOffDate: string;
        readonly DropOffPoint: string;
        readonly PickUpDate: string;
        readonly PickUpPoint: string;
        readonly SIPPCode: string;
    }

    export interface AttractionsBooking extends ProductBooking {
        readonly Attractions: Attraction[];
    }

    export interface Attraction {
        readonly Adults: number;
        readonly Children: number;
        readonly Date: string;
        readonly Destination: Destinations.DestinationSummary;
        readonly Infants: number;
        readonly Title: string;
    }

    export interface AirportParkingBooking extends ProductBooking {
        readonly CarParkName: string;
        readonly DropOffDate: string;
        readonly PickUpDate: string;
    }

    export interface AdHocBooking extends ProductBooking {
    }

    export interface MarhabaBooking extends ProductBooking {
    }

    export interface CruiseBooking extends ProductBooking {
    }

    export interface TravelInsuranceBooking extends ProductBooking {
        readonly Title: string;
    }

    export interface Payment {
        readonly CardFeeAmount: number;
        readonly Cleared: boolean;
        readonly ClientCurrencyCode: string;
        readonly ClientCurrencyExchangeRate: number;
        readonly ClientCurrencyPaymentAmount: number; //Total amount
        readonly PaymentAmount: number;
        readonly PaymentDate: string;
        readonly IsScheduledPayment: boolean;
    }

    export interface PaymentDirect {
        readonly Amount: number;
        readonly Currency: string;
        readonly LocalAmount: number;
        readonly LocalCurrency: string;
    }

    export interface PaymentInformation {
        readonly ClearedPayments: Payment[];
        readonly DirectPayments: PaymentDirect[];
        readonly FuturePayments: Payment[];
    }

    export interface PriceBreakdown {
        readonly CurrencyCode: string;
        readonly Description: string;
        readonly IsPaymentFee: boolean;
        readonly Quantity: number;
        readonly UnitAmount: number;
        readonly IsPriceHidden: boolean;
        readonly SupplierRef: string;
        readonly TypeId: number;
    }
}
